/**
 * TikTok Pixel Utility
 * Handles initialization for TikTok analytics.
 * Note: Only runs in production/live environment (skipped when testMode is true)
 */

import { config } from '../config/env'
import { logger } from './logger'
import { sha256Hex } from './sha256'

/**
 * Initialize the TikTok Pixel base code and fire the initial PageView.
 * Safe to call multiple times — guards against double-init.
 */
export const initializeTikTokPixel = () => {
  if (config.testMode) {
    return
  }

  const pixelId = config.tiktokPixelId

  if (!pixelId || window.ttq) {
    return
  }

  try {
  /* eslint-disable */
  !function (w, d, t) {
    w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(
  var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script")
  ;n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};

    ttq.load(pixelId);
    ttq.page();
  }(window, document, 'ttq');
  /* eslint-enable */
  } catch (error) {
    logger.error('Error initializing TikTok Pixel:', error)
  }
}

export const getTtclid = (funnelData = {}) => {
  if (funnelData.ttclid) return String(funnelData.ttclid)

  try {
    const fromUrl = new URLSearchParams(window.location.search).get('ttclid')
    if (fromUrl) return fromUrl
  } catch (_) {
    // ignore
  }

  const match = document.cookie.match(/(?:^|; )ttclid=([^;]*)/)
  return match ? decodeURIComponent(match[1]) : null
}

const formatPhoneForTikTok = (phone) => {
  if (!phone) return null
  const digits = String(phone).replace(/\D/g, '')
  if (!digits) return null
  if (digits.length === 10) return `+1${digits}`
  if (digits.length >= 11) return `+${digits}`
  return null
}

/**
 * Push hashed PII into the pixel for Advanced Matching.
 * email / phone_number / external_id are SHA-256 hex (TikTok Events API format).
 * ttclid is a click id and must stay unhashed.
 * Safe to call before each event once email/phone exist (e.g. Quiz Completed / checkout).
 */
export const syncTikTokAdvancedMatching = async (funnelData = {}) => {
  if (!config.tiktokPixelId || !window.ttq) return

  const userData = {}

  if (funnelData.email) {
    const hashedEmail = await sha256Hex(String(funnelData.email).trim().toLowerCase())
    if (hashedEmail) userData.email = hashedEmail
  }
  if (funnelData.phone) {
    const phone = formatPhoneForTikTok(funnelData.phone)
    const hashedPhone = phone ? await sha256Hex(phone) : null
    if (hashedPhone) userData.phone_number = hashedPhone
  }
  if (funnelData.entry_id) {
    const hashedExternalId = await sha256Hex(String(funnelData.entry_id))
    if (hashedExternalId) userData.external_id = hashedExternalId
  }

  const ttclid = getTtclid(funnelData)
  if (ttclid) {
    userData.ttclid = ttclid
  }

  if (Object.keys(userData).length === 0) return

  try {
    window.ttq.identify(userData)
    logger.debug('[TikTok] Advanced matching synced', Object.keys(userData))
  } catch (error) {
    logger.error('[TikTok] Advanced matching failed:', error)
  }
}

export default {
  initializeTikTokPixel,
  syncTikTokAdvancedMatching,
  getTtclid,
}
